import React, { useEffect, useState } from 'react';
import { toolsService } from '../../services';
import {
  Settings, Search, Plus, X, ArrowUpRight, ArrowDownLeft,
  AlertTriangle, BarChart2, Wrench, Activity, Building2,
  Edit2, Download, Check, BookOpen, Star
} from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────
const CONDITION_COLORS: Record<string,string> = {
  NEW:          'bg-blue-100 text-blue-700',
  GOOD:         'bg-green-100 text-green-700',
  FAIR:         'bg-amber-100 text-amber-700',
  NEEDS_REPAIR: 'bg-red-100 text-red-700',
  RETIRED:      'bg-gray-100 text-gray-500',
};
const TLS_CATEGORIES = ['TROWEL','FLOAT','DRILL','LEVEL','MEASURE','PPE',
  'VIBRATOR','SCREED','CUTTER','SAFETY','FORMWORK','WELDING','SURVEY','OTHER'];
const REPAIR_TYPES = ['WEAR_AND_TEAR','ACCIDENTAL_DAMAGE','CALIBRATION','ANNUAL_SERVICE','OTHER'];
const SUPPLY_TYPES = ['PURCHASE_ONLY','RENTAL_ONLY','BOTH'];
const RATE_TYPES   = ['PURCHASE','RENTAL_DAILY','RENTAL_WEEKLY'];
const CONDITIONS   = ['NEW','GOOD','FAIR','NEEDS_REPAIR','RETIRED'];

const inp   = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const inpRO = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-400 cursor-not-allowed";
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
const fmtN = (n: any) => n ? Number(n).toLocaleString() : '0';

const exportCSV = (data: any[], filename: string) => {
  if (!data.length) return;
  const keys = Object.keys(data[0]);
  const csv = [keys.join(','), ...data.map(row =>
    keys.map(k => `"${String(row[k]??'').replace(/"/g,'""')}"`).join(',')
  )].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type:'text/csv' }));
  a.download = filename; a.click();
};

const StarRating = ({ value, onChange, readonly=false }: {
  value: number; onChange?: (v:number)=>void; readonly?: boolean;
}) => (
  <div className="flex gap-0.5">
    {[1,2,3,4,5].map(s => (
      <button key={s} type="button" onClick={() => !readonly && onChange?.(s)}
        className={readonly ? 'cursor-default' : 'cursor-pointer'}>
        <Star size={13} className={s<=value ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
      </button>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────
// ── Add/Edit Tool Modal ───────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const ToolModal = ({ tool, onClose, onSaved }: {
  tool?: any; onClose: ()=>void; onSaved: ()=>void;
}) => {
  const isEdit = !!tool;
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [form, setForm]     = useState({
    tls_code:         tool?.tls_code         || '',
    tls_name:         tool?.tls_name         || '',
    tls_category:     tool?.tls_category     || 'OTHER',
    make:             tool?.make             || '',
    serial_number:    tool?.serial_number    || '',
    total_quantity:   tool?.total_quantity   || 1,
    current_location: tool?.current_location || 'Main Store',
    condition:        tool?.condition        || 'GOOD',
    purchase_cost:    tool?.purchase_cost    || '',
    is_active:        tool?.is_active !== undefined ? tool.is_active : true,
  });
  const set = (k:string, v:any) => setForm(f=>({...f,[k]:v}));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      if (isEdit) {
        await api.put(`/tools/${tool.tls_id}`, { ...form, purchase_cost: form.purchase_cost||null });
      } else {
        await api.post('/tools/', { ...form, purchase_cost: form.purchase_cost||null });
      }
      onSaved();
    } catch(err:any) { setError(err.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-bold text-gray-900">{isEdit ? 'Edit Tool' : 'Add Tool'}</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">
                Code {!isEdit && <span className="text-gray-400 font-normal">(auto-generated)</span>}
              </label>
              <input value={isEdit ? form.tls_code : 'Auto-generated on save'}
                onChange={e => isEdit && set('tls_code',e.target.value)}
                readOnly={!isEdit} className={isEdit ? inp : inpRO} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Tool Name *</label>
              <input required value={form.tls_name} onChange={e => set('tls_name',e.target.value)}
                placeholder="Spirit Level 2m" className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Category *</label>
              <select required value={form.tls_category} onChange={e => set('tls_category',e.target.value)} className={inp}>
                {TLS_CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Make / Brand</label>
              <input value={form.make} onChange={e => set('make',e.target.value)}
                placeholder="Stanley, Bosch..." className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Serial Number</label>
              <input value={form.serial_number} onChange={e => set('serial_number',e.target.value)}
                placeholder="SN-XXXX-XXXX" className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Purchase Cost (₹)</label>
              <input type="number" min={0} value={form.purchase_cost}
                onChange={e => set('purchase_cost',e.target.value)} placeholder="Optional" className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {!isEdit && (
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Quantity *</label>
                <input type="number" min={1} required value={form.total_quantity}
                  onChange={e => set('total_quantity',Number(e.target.value))} className={inp} />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Condition</label>
              <select value={form.condition} onChange={e => set('condition',e.target.value)} className={inp}>
                {CONDITIONS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className={isEdit ? 'col-span-2' : ''}>
              <label className="text-xs font-medium text-gray-600 block mb-1">Storage Location</label>
              <input value={form.current_location} onChange={e => set('current_location',e.target.value)} className={inp} />
            </div>
          </div>
          {isEdit && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => set('is_active',e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600" />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Update Tool' : 'Add Tool')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Issue / Return / Damage Modal ────────────────────────────
// ─────────────────────────────────────────────────────────────
const ActionModal = ({ tool, type, onClose, onSaved }: any) => {
  const [qty, setQty]       = useState(1);
  const [projectId, setPrj] = useState('');
  const [saving, setSaving] = useState(false);
  const maxQty = type === 'issue' ? tool.available_qty : tool.issued_qty;

  const cfg: Record<string,any> = {
    issue:  { label:'Issue Tools',  color:'bg-blue-600 hover:bg-blue-700',  desc:`Available: ${tool.available_qty}` },
    return: { label:'Return Tools', color:'bg-green-600 hover:bg-green-700', desc:`Issued: ${tool.issued_qty}` },
    damage: { label:'Mark Damaged', color:'bg-red-600 hover:bg-red-700',    desc:`Issued: ${tool.issued_qty}` },
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true);
    try {
      if (type==='issue')  await toolsService.issue(tool.tls_id, qty);
      if (type==='return') await toolsService.return_(tool.tls_id, qty);
      if (type==='damage') await toolsService.damage(tool.tls_id, qty);
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-80 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-gray-900 capitalize">{type} Tool</h3>
            <p className="text-xs text-gray-400">{tool.tls_name}</p>
          </div>
          <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Quantity</label>
            <input type="number" min={1} max={maxQty} value={qty}
              onChange={e => setQty(Number(e.target.value))} className={inp} />
            <p className="text-xs text-gray-400 mt-1">{cfg[type]?.desc}</p>
          </div>
          {type === 'issue' && (
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Project ID (optional)</label>
              <input type="number" value={projectId} onChange={e => setPrj(e.target.value)}
                placeholder="Link to project" className={inp} />
            </div>
          )}
          <div className="flex gap-3">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving || qty > maxQty}
              className={`flex-1 px-4 py-2 text-white text-sm rounded-lg disabled:opacity-50 ${cfg[type]?.color}`}>
              {saving ? '...' : cfg[type]?.label}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Tool Detail Modal (Stock Register + Repair Log) ───────────
// ─────────────────────────────────────────────────────────────
const ToolDetailModal = ({ tool, onClose }: { tool: any; onClose: ()=>void }) => {
  const [tab, setTab]         = useState<'stock'|'repair'>('stock');
  const [stockLog, setStock]  = useState<any[]>([]);
  const [repairLog, setRepair]= useState<any[]>([]);
  const [showRepairForm, setShowRepair] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [repairForm, setRepairForm] = useState({
    sent_date: new Date().toISOString().split('T')[0],
    repair_type: 'WEAR_AND_TEAR', description: '',
    repair_cost: 0, repair_vendor: '', qty: 1
  });

  const loadTab = async (t: string) => {
    const { default: api } = await import('../../utils/api');
    if (t === 'stock') {
      const r = await api.get(`/tools/${tool.tls_id}/stock`);
      setStock(r.data.data);
    } else {
      const r = await api.get(`/tools/${tool.tls_id}/repair`);
      setRepair(r.data.data);
    }
  };

  useEffect(() => { loadTab(tab); }, [tab]);

  const saveRepair = async () => {
    setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      await api.post(`/tools/${tool.tls_id}/repair`, repairForm);
      setShowRepair(false); loadTab('repair');
    } catch(e:any) { setError(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  const TX_COLORS: Record<string,string> = {
    PURCHASE: 'text-green-600', ISSUE: 'text-blue-600',
    RETURN: 'text-green-600', DAMAGE: 'text-red-600',
    REPAIR_OUT: 'text-amber-600', REPAIR_IN: 'text-green-600',
    WRITE_OFF: 'text-red-700'
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-gray-900">{tool.tls_name}</h3>
            <p className="text-xs text-gray-400">{tool.tls_code} · {tool.tls_category}
              {tool.serial_number && ` · SN: ${tool.serial_number}`}
            </p>
          </div>
          <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
        </div>
        {/* Quick stats */}
        <div className="grid grid-cols-4 gap-3 px-6 py-3 bg-gray-50 border-b border-gray-100">
          {[
            { label:'Available', val:tool.available_qty, color:'text-green-600' },
            { label:'Issued',    val:tool.issued_qty,    color:'text-blue-600' },
            { label:'Damaged',   val:tool.damaged_qty,   color:'text-red-600' },
            { label:'In Repair', val:tool.under_repair_qty, color:'text-amber-600' },
          ].map(s => (
            <div key={s.label} className="text-center">
              <p className={`text-xl font-bold ${s.color}`}>{s.val}</p>
              <p className="text-xs text-gray-400">{s.label}</p>
            </div>
          ))}
        </div>
        {/* Sub-tabs */}
        <div className="flex gap-1 px-6 pt-3 border-b border-gray-100">
          {[{key:'stock',label:'Stock Register'},{key:'repair',label:'Repair Log'}].map(t => (
            <button key={t.key} onClick={() => setTab(t.key as any)}
              className={`px-4 py-2 text-xs font-medium rounded-t-lg transition-colors
                ${tab===t.key ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}>
              {t.label}
            </button>
          ))}
        </div>
        {error && <div className="mx-6 mt-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}

        <div className="flex-1 overflow-y-auto p-6">
          {/* ── STOCK REGISTER ── */}
          {tab === 'stock' && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Date</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Type</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Qty</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Balance</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">By</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Remarks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stockLog.length === 0 ? (
                    <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">No stock transactions yet.</td></tr>
                  ) : stockLog.map(s => (
                    <tr key={s.stock_id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500 text-xs">{fmtDate(s.transaction_date)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs font-medium ${TX_COLORS[s.transaction_type]||'text-gray-600'}`}>
                          {s.transaction_type}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-800">{s.quantity}</td>
                      <td className="px-3 py-2 text-gray-600">{s.balance_after}</td>
                      <td className="px-3 py-2 text-gray-400 text-xs">{s.performed_by_name||'—'}</td>
                      <td className="px-3 py-2 text-gray-400 text-xs">{s.remarks||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── REPAIR LOG ── */}
          {tab === 'repair' && (
            <>
              <div className="flex justify-between mb-3">
                <p className="text-sm text-gray-500">{repairLog.length} repair records</p>
                <button onClick={() => setShowRepair(!showRepairForm)}
                  disabled={tool.damaged_qty === 0}
                  className="flex items-center gap-1 text-xs bg-amber-500 text-white px-3 py-1.5 rounded-lg hover:bg-amber-600 disabled:opacity-40">
                  <Wrench size={12}/> Send for Repair
                </button>
              </div>
              {showRepairForm && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Sent Date *</label>
                      <input type="date" value={repairForm.sent_date}
                        onChange={e => setRepairForm(f=>({...f,sent_date:e.target.value}))} className={inp} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Repair Type *</label>
                      <select value={repairForm.repair_type}
                        onChange={e => setRepairForm(f=>({...f,repair_type:e.target.value}))} className={inp}>
                        {REPAIR_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Description *</label>
                    <textarea rows={2} value={repairForm.description}
                      onChange={e => setRepairForm(f=>({...f,description:e.target.value}))} className={inp} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Qty</label>
                      <input type="number" min={1} max={tool.damaged_qty} value={repairForm.qty}
                        onChange={e => setRepairForm(f=>({...f,qty:Number(e.target.value)}))} className={inp} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Est. Cost (₹)</label>
                      <input type="number" value={repairForm.repair_cost}
                        onChange={e => setRepairForm(f=>({...f,repair_cost:Number(e.target.value)}))} className={inp} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Vendor</label>
                      <input value={repairForm.repair_vendor}
                        onChange={e => setRepairForm(f=>({...f,repair_vendor:e.target.value}))} className={inp} />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setShowRepair(false)}
                      className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg">Cancel</button>
                    <button onClick={saveRepair} disabled={saving}
                      className="px-3 py-1.5 text-xs bg-amber-500 text-white rounded-lg disabled:opacity-50">
                      {saving ? 'Saving...' : 'Send for Repair'}
                    </button>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                {repairLog.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No repair records yet.</p>
                ) : repairLog.map(r => (
                  <div key={r.repair_id}
                    className={`border rounded-xl p-3 ${r.returned_date ? 'border-green-100 bg-green-50' : 'border-amber-100 bg-amber-50'}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="text-xs font-medium text-gray-600">{r.repair_type}</span>
                        <span className="text-xs text-gray-400 ml-2">Sent: {fmtDate(r.sent_date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.repair_cost > 0 && <span className="text-xs font-semibold text-gray-700">₹{fmtN(r.repair_cost)}</span>}
                        {r.returned_date
                          ? <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">✅ Returned</span>
                          : <span className="text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">🔧 In Repair</span>}
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">{r.description}</p>
                    <div className="flex gap-4 mt-1 text-xs text-gray-400">
                      {r.repair_vendor && <span>Vendor: {r.repair_vendor}</span>}
                      {r.returned_date && <span>Returned: {fmtDate(r.returned_date)}</span>}
                      {r.condition_after && <span>Condition: {r.condition_after}</span>}
                      {r.is_write_off && <span className="text-red-600">⚠ Written off</span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Supplier Modal ────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const SupplierModal = ({ supplier, onClose, onSaved }: {
  supplier?: any; onClose: ()=>void; onSaved: ()=>void;
}) => {
  const isEdit = !!supplier;
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [form, setForm]     = useState({
    tsp_code:       supplier?.tsp_code       || '',
    company_name:   supplier?.company_name   || '',
    contact_person: supplier?.contact_person || '',
    mobile:         supplier?.mobile         || '',
    email:          supplier?.email          || '',
    gstin:          supplier?.gstin          || '',
    address:        supplier?.address        || '',
    city:           supplier?.city           || 'Bengaluru',
    payment_terms:  supplier?.payment_terms  || 'ON_DELIVERY',
    supply_type:    supplier?.supply_type    || 'BOTH',
    rating:         supplier?.rating         || 0,
    is_active:      supplier?.is_active !== undefined ? supplier.is_active : true,
  });
  const set = (k:string, v:any) => setForm(f=>({...f,[k]:v}));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      if (isEdit) { await api.put(`/tools/suppliers/${supplier.tsp_id}`, form); }
      else        { await api.post('/tools/suppliers', form); }
      onSaved();
    } catch(err:any) { setError(err.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-bold text-gray-900">{isEdit ? 'Edit Supplier' : 'Add Tool Supplier'}</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">
                Code {!isEdit && <span className="text-gray-400 font-normal">(auto)</span>}
              </label>
              <input value={isEdit ? form.tsp_code : 'Auto-generated'}
                onChange={e => isEdit && set('tsp_code',e.target.value)}
                readOnly={!isEdit} className={isEdit ? inp : inpRO} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Company Name *</label>
              <input required value={form.company_name} onChange={e => set('company_name',e.target.value)} className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Contact Person *</label>
              <input required value={form.contact_person} onChange={e => set('contact_person',e.target.value)} className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Mobile *</label>
              <input required value={form.mobile} onChange={e => set('mobile',e.target.value)} className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Email</label>
              <input type="email" value={form.email} onChange={e => set('email',e.target.value)} className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">GSTIN</label>
              <input value={form.gstin} onChange={e => set('gstin',e.target.value)} className={inp} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">City</label>
              <input value={form.city} onChange={e => set('city',e.target.value)} className={inp} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Payment Terms</label>
              <select value={form.payment_terms} onChange={e => set('payment_terms',e.target.value)} className={inp}>
                {['ON_DELIVERY','NET_15','NET_30','ADVANCE'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Supply Type</label>
              <select value={form.supply_type} onChange={e => set('supply_type',e.target.value)} className={inp}>
                {SUPPLY_TYPES.map(t => <option key={t}>{t.replace('_',' ')}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Rating</label>
            <StarRating value={form.rating} onChange={v => set('rating',v)} />
          </div>
          {isEdit && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => set('is_active',e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600" />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Add Supplier')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Tools Tab ─────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const ToolsTab = () => {
  const [tools, setTools]           = useState<any[]>([]);
  const [stats, setStats]           = useState<any>(null);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [catFilter, setCat]         = useState('');
  const [condFilter, setCond]       = useState('');
  const [showInactive, setInactive] = useState(false);
  const [showAdd, setShowAdd]       = useState(false);
  const [editTool, setEditTool]     = useState<any>(null);
  const [action, setAction]         = useState<any>(null);
  const [detailTool, setDetailTool] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, sRes] = await Promise.all([
        toolsService.getAll({ category: catFilter||undefined, condition: condFilter||undefined, search: search||undefined }),
        toolsService.getStats(),
      ]);
      let data = tRes.data.data;
      if (!showInactive) data = data.filter((t:any) => t.is_active !== false);
      setTools(data); setStats(sRes.data.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [catFilter, condFilter, showInactive]);

  return (
    <>
      {showAdd && <ToolModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editTool && <ToolModal tool={editTool} onClose={() => setEditTool(null)} onSaved={() => { setEditTool(null); load(); }} />}
      {action && <ActionModal tool={action.tool} type={action.type} onClose={() => setAction(null)} onSaved={() => { setAction(null); load(); }} />}
      {detailTool && <ToolDetailModal tool={detailTool} onClose={() => setDetailTool(null)} />}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">
            {stats ? `${fmtN(stats.total_tools)} tools · ${fmtN(stats.total_qty)} total qty` : ''}
          </p>
          <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-500">
            <input type="checkbox" checked={showInactive} onChange={e => setInactive(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-gray-300" />
            Show inactive
          </label>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportCSV(tools.map(t=>({
            Code:t.tls_code, Name:t.tls_name, Category:t.tls_category,
            Make:t.make||'', Serial:t.serial_number||'',
            Total:t.total_quantity, Available:t.available_qty,
            Issued:t.issued_qty, Damaged:t.damaged_qty,
            InRepair:t.under_repair_qty, Location:t.current_location,
            Condition:t.condition, Cost:t.purchase_cost||''
          })),'tools.csv')}
            className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
            <Download size={14}/> Export
          </button>
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <Plus size={15}/> Add Tool
          </button>
        </div>
      </div>

      {/* Alert for needs repair */}
      {stats && Number(stats.needs_repair_count) > 0 && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700 mb-4">
          <AlertTriangle size={14}/> {stats.needs_repair_count} tool(s) need repair
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-3">
        {['', ...TLS_CATEGORIES].map(c => (
          <button key={c} onClick={() => setCat(c)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
              ${catFilter===c ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'}`}>
            {c || 'All Categories'}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {['', ...CONDITIONS].map(c => (
          <button key={c} onClick={() => setCond(c)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
              ${condFilter===c ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-200'}`}>
            {c || 'All Conditions'}
          </button>
        ))}
      </div>

      {/* Search */}
      <form onSubmit={e => { e.preventDefault(); load(); }} className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tools..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <button type="submit" className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Search</button>
      </form>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Tool</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Category</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Total</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Available</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Issued</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Damaged</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Repair</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Location</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Condition</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? [...Array(6)].map((_,i) => (
              <tr key={i}>{[...Array(10)].map((_,j) => (
                <td key={j} className="px-4 py-3"><div className="bg-gray-100 rounded h-4 animate-pulse"/></td>
              ))}</tr>
            )) : tools.map(t => (
              <tr key={t.tls_id} className={`hover:bg-gray-50 ${t.is_active===false ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{t.tls_name}</div>
                  <div className="text-xs text-gray-400">{t.tls_code}{t.make ? ` · ${t.make}` : ''}</div>
                  {t.serial_number && <div className="text-xs text-gray-300">SN: {t.serial_number}</div>}
                </td>
                <td className="px-4 py-3 text-gray-600">{t.tls_category}</td>
                <td className="px-4 py-3 text-center font-medium text-gray-700">{t.total_quantity}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`font-medium ${t.available_qty > 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {t.available_qty}
                  </span>
                </td>
                <td className="px-4 py-3 text-center text-blue-600 font-medium">{t.issued_qty}</td>
                <td className="px-4 py-3 text-center text-red-500 font-medium">{t.damaged_qty}</td>
                <td className="px-4 py-3 text-center text-amber-500 font-medium">{t.under_repair_qty}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{t.current_location}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${CONDITION_COLORS[t.condition]||''}`}>
                    {t.condition}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => setAction({tool:t,type:'issue'})} disabled={t.available_qty===0}
                      title="Issue" className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 disabled:opacity-30">
                      <ArrowUpRight size={14}/>
                    </button>
                    <button onClick={() => setAction({tool:t,type:'return'})} disabled={t.issued_qty===0}
                      title="Return" className="p-1.5 rounded-lg hover:bg-green-50 text-green-600 disabled:opacity-30">
                      <ArrowDownLeft size={14}/>
                    </button>
                    <button onClick={() => setAction({tool:t,type:'damage'})} disabled={t.issued_qty===0}
                      title="Damage" className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 disabled:opacity-30">
                      <AlertTriangle size={14}/>
                    </button>
                    <button onClick={() => setEditTool(t)} title="Edit"
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                      <Edit2 size={14}/>
                    </button>
                    <button onClick={() => setDetailTool(t)} title="Detail"
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                      <BookOpen size={14}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && tools.length === 0 && (
          <div className="text-center py-12 text-gray-400">No tools found.</div>
        )}
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Suppliers Tab ─────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const SuppliersTab = () => {
  const [suppliers, setSuppliers]       = useState<any[]>([]);
  const [loading, setLoading]           = useState(true);
  const [showAdd, setShowAdd]           = useState(false);
  const [editSupplier, setEditSupplier] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { default: api } = await import('../../utils/api');
      const r = await api.get('/tools/suppliers');
      setSuppliers(r.data.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <>
      {showAdd && <SupplierModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editSupplier && <SupplierModal supplier={editSupplier} onClose={() => setEditSupplier(null)}
        onSaved={() => { setEditSupplier(null); load(); }} />}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{suppliers.length} tool suppliers</p>
        <div className="flex gap-2">
          <button onClick={() => exportCSV(suppliers.map(s=>({
            Code:s.tsp_code, Company:s.company_name, Contact:s.contact_person,
            Mobile:s.mobile, City:s.city, SupplyType:s.supply_type,
            Payment:s.payment_terms, Rating:s.rating||0, Rates:s.rate_count||0
          })),'tool_suppliers.csv')}
            className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
            <Download size={14}/> Export
          </button>
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <Plus size={15}/> Add Supplier
          </button>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Supplier</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Contact</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">City</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Supply Type</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Payment</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Rating</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Rates</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? [...Array(3)].map((_,i) => (
              <tr key={i}>{[...Array(8)].map((_,j) => (
                <td key={j} className="px-4 py-3"><div className="bg-gray-100 rounded h-4 animate-pulse"/></td>
              ))}</tr>
            )) : suppliers.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                No tool suppliers yet.
              </td></tr>
            ) : suppliers.map(s => (
              <tr key={s.tsp_id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{s.company_name}</div>
                  <div className="text-xs text-gray-400">{s.tsp_code}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-gray-700">{s.contact_person}</div>
                  <div className="text-xs text-gray-400">{s.mobile}</div>
                </td>
                <td className="px-4 py-3 text-gray-600 text-xs">{s.city}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full
                    ${s.supply_type==='PURCHASE_ONLY' ? 'bg-blue-100 text-blue-700' :
                      s.supply_type==='RENTAL_ONLY' ? 'bg-amber-100 text-amber-700' :
                      'bg-green-100 text-green-700'}`}>
                    {s.supply_type?.replace('_',' ')}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{s.payment_terms?.replace('_',' ')}</td>
                <td className="px-4 py-3"><StarRating value={s.rating||0} readonly /></td>
                <td className="px-4 py-3 text-xs text-gray-500">{s.rate_count||0} rates</td>
                <td className="px-4 py-3">
                  <button onClick={() => setEditSupplier(s)}
                    className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-lg hover:bg-blue-100">
                    <Edit2 size={11}/> Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Activity Mapping Tab ──────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const ActivityMappingTab = () => {
  const [mappings, setMappings]   = useState<any[]>([]);
  const [filtered, setFiltered]   = useState<any[]>([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [catFilter, setCatFilter] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { default: api } = await import('../../utils/api');
        const r = await api.get('/tools/activity-mappings');
        setMappings(r.data.data||[]); setFiltered(r.data.data||[]);
      } catch { setMappings([]); setFiltered([]); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  useEffect(() => {
    let data = mappings;
    if (catFilter) data = data.filter((m:any) => m.tls_category === catFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter((m:any) =>
        m.activity_name?.toLowerCase().includes(q) ||
        m.act_code?.toLowerCase().includes(q) ||
        m.tls_category?.toLowerCase().includes(q)
      );
    }
    setFiltered(data);
  }, [search, catFilter, mappings]);

  const grouped = filtered.reduce((acc:any, row:any) => {
    const key = row.act_code;
    if (!acc[key]) acc[key] = { act_code:row.act_code, activity_name:row.activity_name, items:[] };
    acc[key].items.push(row);
    return acc;
  }, {});

  const cats = mappings.map((m:any) => m.tls_category)
    .filter((c:string, i:number, a:string[]) => a.indexOf(c)===i).sort();

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{mappings.length} activity-tool mappings</p>
        <button onClick={() => exportCSV(mappings.map(m=>({
          ActivityCode:m.act_code, ActivityName:m.activity_name,
          ToolCategory:m.tls_category, QtyPerWorker:m.qty_per_worker,
          QtyPerTeam:m.qty_per_team||'', Consumable:m.is_consumable?'Yes':'No',
          SafetyPPE:m.is_safety_ppe?'Yes':'No', Mandatory:m.is_mandatory?'Yes':'No'
        })),'activity_tool_map.csv')}
          className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
          <Download size={14}/> Export
        </button>
      </div>
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search activity or tool category..."
            className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"/>
        </div>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Categories</option>
          {cats.map(c => <option key={c as string}>{c as string}</option>)}
        </select>
      </div>
      {loading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_,i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
              <div className="bg-gray-100 rounded h-4 w-48 mb-2"/>
              <div className="bg-gray-100 rounded h-3 w-32"/>
            </div>
          ))}
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-center py-12 text-gray-400">No mappings found.</div>
      ) : (
        <div className="space-y-2">
          {Object.values(grouped).map((grp:any) => (
            <div key={grp.act_code} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded mr-2">
                    {grp.act_code}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{grp.activity_name}</span>
                </div>
                <span className="text-xs text-gray-400">{grp.items.length} tool type{grp.items.length>1?'s':''}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {grp.items.map((m:any, i:number) => (
                  <div key={i} className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs border
                    ${m.is_mandatory ? 'bg-red-50 border-red-200' :
                      m.is_safety_ppe ? 'bg-yellow-50 border-yellow-200' :
                      m.is_consumable ? 'bg-blue-50 border-blue-200' :
                      'bg-gray-50 border-gray-200'}`}>
                    <Settings size={11} className="text-gray-400"/>
                    <span className="font-medium text-gray-800">{m.tls_category}</span>
                    <span className="text-gray-500">{m.qty_per_worker}/worker</span>
                    {m.is_safety_ppe && <span className="text-yellow-600 font-medium">🦺 PPE</span>}
                    {m.is_consumable && <span className="text-blue-600">consumable</span>}
                    {m.is_mandatory && <span className="text-red-600 font-bold">*</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Summary Tab ───────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const SummaryTab = () => {
  const [stats, setStats]     = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    toolsService.getStats().then(r => { setStats(r.data.data); setLoading(false); });
  }, []);
  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;
  if (!stats) return null;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label:'Total Tools',  value:fmtN(stats.total_tools),    color:'bg-blue-600 text-white' },
          { label:'Total Qty',    value:fmtN(stats.total_qty),      color:'bg-indigo-600 text-white' },
          { label:'Available',    value:fmtN(stats.available_qty),  color:'bg-green-600 text-white' },
          { label:'Issued',       value:fmtN(stats.issued_qty),     color:'bg-amber-500 text-white' },
          { label:'Damaged',      value:fmtN(stats.damaged_qty),    color:'bg-red-500 text-white' },
        ].map(c => (
          <div key={c.label} className={`${c.color} rounded-xl p-4 text-center`}>
            <p className="text-2xl font-bold">{c.value}</p>
            <p className="text-xs font-medium mt-1 opacity-90">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Stock Health</h3>
          <div className="space-y-2">
            {[
              { label:'In Store',    val:stats.available_qty,   color:'bg-green-500' },
              { label:'On Site',     val:stats.issued_qty,      color:'bg-blue-500' },
              { label:'Damaged',     val:stats.damaged_qty,     color:'bg-red-500' },
              { label:'In Repair',   val:stats.under_repair_qty,color:'bg-amber-500' },
            ].map(s => {
              const pct = stats.total_qty > 0 ? (Number(s.val)/Number(stats.total_qty))*100 : 0;
              return (
                <div key={s.label} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-20">{s.label}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2">
                    <div className={`${s.color} h-2 rounded-full`} style={{width:`${pct}%`}}/>
                  </div>
                  <span className="text-xs font-semibold text-gray-700 w-8 text-right">{fmtN(s.val)}</span>
                </div>
              );
            })}
          </div>
          {Number(stats.needs_repair_count) > 0 && (
            <div className="mt-3 flex items-center gap-2 bg-red-50 rounded-lg px-3 py-2 text-xs text-red-700">
              <AlertTriangle size={12}/> {stats.needs_repair_count} tools marked NEEDS_REPAIR
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Tools by Category</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {stats.by_category && Object.entries(stats.by_category)
              .sort(([,a]:any,[,b]:any) => b-a)
              .map(([cat,cnt]:any) => (
                <div key={cat} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-20 truncate">{cat}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full"
                      style={{width:`${(cnt/Number(stats.total_tools))*100}%`}}/>
                  </div>
                  <span className="text-xs font-semibold text-gray-700 w-6 text-right">{cnt}</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ── Main ToolsPage ────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
const ToolsPage = () => {
  const [tab, setTab] = useState<'tools'|'suppliers'|'mapping'|'summary'>('tools');
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Tools Master</h1>
        <p className="text-sm text-gray-500 mt-0.5">Tool inventory · Issue/Return · Repair · Suppliers · Activity Mapping</p>
      </div>
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-6">
        {([
          { key:'tools',    label:'Tools',            icon:<Settings size={14}/> },
          { key:'suppliers',label:'Suppliers',        icon:<Building2 size={14}/> },
          { key:'mapping',  label:'Activity Mapping', icon:<Activity size={14}/> },
          { key:'summary',  label:'Summary',          icon:<BarChart2 size={14}/> },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-colors
              ${tab===t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>
      {tab === 'tools'    && <ToolsTab />}
      {tab === 'suppliers'&& <SuppliersTab />}
      {tab === 'mapping'  && <ActivityMappingTab />}
      {tab === 'summary'  && <SummaryTab />}
    </div>
  );
};

export default ToolsPage;
